import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { formatINR, formatNum, todayStr, gradeClasses } from "@/lib/format";
import { MetricCard, SectionTitle } from "@/components/Shared";
import {
  Gauge, IndianRupee, PhoneCall, Users, Stethoscope, TrendingUp,
  Trophy, AlertTriangle, UserCheck, UserX, Ban,
} from "lucide-react";

const PERIODS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
];

function PerfCard({ p, rank, variant }) {
  return (
    <div className="flex items-center gap-3 surface-2 p-3" data-testid={`perf-${variant}-${rank}`}>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center font-heading font-bold text-sm ${variant === "top" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
        #{rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-100 truncate text-sm">{p.name}</div>
        <div className="text-xs text-slate-500 font-mono">{formatINR(p.metrics?.revenue || 0)} · {p.metrics?.total_visits || 0} visits</div>
      </div>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-md border ${gradeClasses[p.grade]}`}>{p.grade}</span>
      <span className="font-mono font-bold text-white">{p.score}</span>
    </div>
  );
}

export default function Overview() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);

  useEffect(() => { api.overview(period, todayStr()).then(setData); }, [period]);

  const t = data?.team || {};

  const topReasons = useMemo(() => {
    const r = t.reasons || {};
    const total = Object.values(r).reduce((a, b) => a + b, 0);
    return Object.entries(r)
      .map(([name, count]) => ({ name, count, pct: total ? Math.round((count / total) * 100) : 0 }))
      .sort((a, b) => b.count - a.count).slice(0, 6);
  }, [t]);

  return (
    <div className="animate-fade-up">
      <SectionTitle
        icon={Gauge} title="Overview Snapshot" desc="Live team performance at a glance"
        right={
          <div className="flex gap-1 bg-white/5 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button key={p.id} data-testid={`period-${p.id}`} onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${period === p.id ? "bg-[#C9A24B] text-[#0b1120]" : "text-slate-400 hover:text-slate-200"}`}>
                {p.label}
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <MetricCard label="Total Revenue" value={formatINR(t.revenue || 0)} sub={`₹${formatNum(t.rev_per_visit || 0)} / visit`} icon={IndianRupee} tone="gold" />
        <MetricCard label="Dialed Calls" value={formatNum(t.dialed || 0)} sub={`${t.connect_rate || 0}% connect rate`} icon={PhoneCall} tone="sky" />
        <MetricCard label="Patient Visits" value={formatNum(t.total_visits || 0)} sub={`${t.new_visits || 0} new · ${t.old_visits || 0} old`} icon={Users} tone="indigo" />
        <MetricCard label="Treatments" value={formatNum(t.total_treated || 0)} sub={`${t.treat_conv || 0}% conversion`} icon={Stethoscope} tone="emerald" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Leads" value={formatNum(t.leads || 0)} sub={`${t.lead_conv || 0}% lead→visit`} icon={TrendingUp} tone="rose" />
        <MetricCard label="Connected" value={formatNum(t.connected || 0)} icon={PhoneCall} tone="sky" />
        <MetricCard label="Active Headcount" value={formatNum(data?.active_count || 0)} sub={`${data?.inactive_count || 0} inactive`} icon={UserCheck} tone="emerald" />
        <MetricCard label="Working Days" value={formatNum(t.working_days || 0)} icon={Gauge} tone="indigo" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        <div className="surface p-5">
          <div className="flex items-center gap-2 font-heading text-lg font-light text-white mb-3"><Trophy className="h-5 w-5 text-emerald-400" /> Top Performers</div>
          <div className="space-y-2">
            {(data?.top || []).map((p, i) => <PerfCard key={p.employee_id} p={p} rank={i + 1} variant="top" />)}
            {(!data?.top || data.top.length === 0) && <div className="text-sm text-slate-500 py-4 text-center">No data yet</div>}
          </div>
        </div>

        <div className="surface p-5">
          <div className="flex items-center gap-2 font-heading text-lg font-light text-white mb-3"><AlertTriangle className="h-5 w-5 text-rose-400" /> Needs Attention</div>
          <div className="space-y-2">
            {(data?.bottom || []).map((p, i) => <PerfCard key={p.employee_id} p={p} rank={i + 1} variant="bottom" />)}
            {(!data?.bottom || data.bottom.length === 0) && <div className="text-sm text-slate-500 py-4 text-center">No data yet</div>}
          </div>
        </div>

        <div className="surface p-5">
          <div className="flex items-center gap-2 font-heading text-lg font-light text-white mb-3"><UserX className="h-5 w-5 text-amber-400" /> Not Filled Today</div>
          {(data?.not_filled || []).length === 0 ? (
            <div className="text-sm text-emerald-400 py-4 text-center font-semibold">🎉 Everyone has filled today!</div>
          ) : (
            <div className="space-y-2">
              {data.not_filled.map((e) => (
                <div key={e.id} className="flex items-center justify-between surface-2 px-3 py-2" data-testid={`not-filled-${e.id}`}>
                  <span className="text-sm font-semibold text-slate-200">{e.name}</span>
                  {e.phone && <a href={`https://wa.me/91${e.phone}?text=Please%20fill%20today's%20sales%20entry`} target="_blank" rel="noreferrer" className="text-xs text-[#C9A24B] font-semibold hover:underline">Nudge</a>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top reasons for no treatment */}
      <div className="surface p-5">
        <div className="flex items-center gap-2 font-heading text-lg font-light text-white mb-4"><Ban className="h-5 w-5 text-rose-400" /> Top Reasons Patients Didn't Take Treatment</div>
        {topReasons.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">No no-treatment records for this period 🎉</div>
        ) : (
          <div className="space-y-3">
            {topReasons.map((r, i) => (
              <div key={r.name} data-testid={`reason-bar-${i}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold text-slate-200">{r.name}</span>
                  <span className="text-slate-400 font-mono">{r.count} <span className="text-slate-600">({r.pct}%)</span></span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${r.pct}%`, background: ["#C9A24B", "#38BDF8", "#818CF8", "#34D399", "#F472B6", "#FB923C"][i % 6] }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
